import React, { useEffect, useMemo, useRef } from 'react';
import * as Sentry from '@sentry/react';
import { Row, Content, Message, useLocation } from 'components/lib';

export function NotFound(props) {
  const location = useLocation();
  const hasPreFilledRef = useRef(false);

  const pageUrl =
    location.state?.url ||
    location.state?.originalUrl ||
    (typeof window !== 'undefined' ? window.location.href : '');

  const actualPath = useMemo(() => {
    if (location.state?.path) return location.state.path;
    if (pageUrl) {
      try {
        const parsed = new URL(
          pageUrl,
          typeof window !== 'undefined' ? window.location.origin : 'http://localhost'
        );
        if (parsed.pathname && parsed.pathname !== '/notfound') {
          return parsed.pathname;
        }
      } catch (e) {
        if (pageUrl.startsWith('/') && pageUrl !== '/notfound') {
          return pageUrl.split('?')[0];
        }
      }
    }
    return location.pathname;
  }, [pageUrl, location.pathname, location.state?.path]);

  const searchParams = useMemo(
    () => new URLSearchParams(location.search),
    [location.search]
  );

  const errorMessage =
    location.state?.error ||
    location.state?.message ||
    searchParams.get('error') ||
    searchParams.get('message');

  const details = location.state?.details;

  const crispMessage = useMemo(() => {
    const lines = [
      'Hi Support,',
      '',
      'I am facing an error on page:',
      pageUrl,
      '',
      'Error details:',
      errorMessage
        ? `• Status: ${location.state?.status || 'Error'}`
        : '• Status: 404 Not Found (Page does not exist)',
      errorMessage ? `• Message: ${errorMessage}` : null,
      `• Path: ${actualPath}`,
    ];

    if (details) {
      lines.push(
        `• More logs: ${
          typeof details === 'object'
            ? JSON.stringify(details, null, 2)
            : String(details)
        }`
      );
    }

    lines.push('', 'Could you please help look into this?');

    return lines
      .filter((line) => line !== null && line !== undefined)
      .join('\n')
      .trim();
  }, [
    pageUrl,
    errorMessage,
    details,
    actualPath,
    location.state?.status,
  ]);

  // Report to Sentry whenever this error page is showing up
  useEffect(() => {
    try {
      const referrer =
        typeof document !== 'undefined'
          ? document.referrer || 'direct'
          : 'direct';

      Sentry.withScope((scope) => {
        scope.setTag('page_url', pageUrl);
        scope.setTag('path', actualPath);
        scope.setTag(
          'error_type',
          errorMessage ? 'application_error' : '404_not_found'
        );
        if (location.state?.status) {
          scope.setTag('status_code', String(location.state.status));
        }
        scope.setExtra('url', pageUrl);
        scope.setExtra('path', actualPath);
        scope.setExtra('referrer', referrer);
        if (location.state) {
          scope.setExtra('navigation_state', location.state);
        }
        if (details) {
          scope.setExtra('details', details);
        }
        if (errorMessage) {
          scope.setExtra('errorMessage', errorMessage);
        }

        const sentryMessage = errorMessage
          ? `Error page shown on ${actualPath}: ${
              typeof errorMessage === 'string'
                ? errorMessage
                : JSON.stringify(errorMessage)
            }`
          : `404 Not Found page shown on ${actualPath} (${pageUrl})`;

        Sentry.captureMessage(sentryMessage, 'warning');
      });
    } catch (err) {
      console.error('Failed to report 404 to Sentry:', err);
    }
  }, []); // Run once on mount

  // Clean up pre-filled Crisp message text when unmounting
  useEffect(() => {
    return () => {
      if (
        hasPreFilledRef.current &&
        typeof window !== 'undefined' &&
        window.$crisp
      ) {
        window.$crisp.push(['set', 'message:text', ['']]);
      }
    };
  }, []);

  const handleContactSupport = () => {
    hasPreFilledRef.current = true;
    if (typeof window !== 'undefined') {
      if (window.$crisp) {
        window.$crisp.push(['do', 'chat:show']);
        window.$crisp.push(['set', 'message:text', [crispMessage]]);
        window.$crisp.push(['do', 'chat:open']);
      } else {
        window.location.href = `mailto:support@salesblink.io?subject=Error on ${encodeURIComponent(
          pageUrl
        )}&body=${encodeURIComponent(crispMessage)}`;
      }
      window.dispatchEvent(new Event('crisp:openWidget'));
    }
  };

  const messageText = errorMessage
    ? `${errorMessage}. If you believe this is a mistake, you can contact our support team.`
    : "Oops, the page you're looking for doesn't exist. If you think this is a mistake, you can contact our support team.";

  return (
    <Row>
      <Content>
        <Message
          type="error"
          title="404"
          text={messageText}
          buttonText="Go Home"
          buttonLink="/"
          secondaryButtonText="Contact Support"
          secondaryButtonAction={handleContactSupport}
        />
      </Content>
    </Row>
  );
}
